import area from '@turf/area';
import kinks from '@turf/kinks';


// --- UTIL: ubah "12,34" -> "12.34" (semua koma)
const toDot = (s) => s.replace(/,/g, '.');

// --- Parse satu koordinat (desimal atau DMS) + optional hemisphere (awal/akhir)
function parseSingleCoord(raw) {
  const s = raw.trim();

  // Ambil hemisphere bila ada di awal ATAU akhir
  const hemiMatch =
    s.match(/^\s*([NSEW])\b/i) || // awal
    s.match(/([NSEW])\s*$/i);     // akhir
  const hemi = hemiMatch ? hemiMatch[1].toUpperCase() : null;

  // Ambil semua angka (deg, min, sec) — mendukung titik/koma desimal
  const numMatches = [...s.matchAll(/[-+]?\d+(?:[.,]\d+)?/g)].map(m => toDot(m[0]));
  if (numMatches.length === 0) return { value: null, hemi: null };

  // Deg, Min, Sec (opsional)
  let deg = parseFloat(numMatches[0]);
  let minutes = 0, seconds = 0;

  // Prioritaskan interpretasi DMS bila terindikasi
  const looksDMS =
    /[°º]/.test(s) ||                 // ada simbol derajat
    /['’]|["”]/.test(s) ||            // ada simbol menit/detik
    (numMatches.length > 1);          // ada ≥2 angka → kemungkinan DMS

  if (looksDMS) {
    if (numMatches.length >= 2) minutes = parseFloat(numMatches[1]);
    if (numMatches.length >= 3) seconds = parseFloat(numMatches[2]);

    // Validasi komponen DMS (bila ada)
    if (minutes < 0 || minutes >= 60) return { value: null, hemi: null };
    if (seconds < 0 || seconds >= 60) return { value: null, hemi: null };

    const sign = Math.sign(deg);
    deg = Math.abs(deg);
    let val = deg + minutes / 60 + seconds / 3600;

    // Terapkan hemisphere / sign
    if (hemi === 'S' || hemi === 'W') val = -Math.abs(val);
    else if (hemi === 'N' || hemi === 'E') val = Math.abs(val);
    else if (sign < 0) val = -val;

    return { value: val, hemi };
  }

  // Desimal simpel
  const val = parseFloat(numMatches[0]);
  return { value: val, hemi };
}

// --- Validator akhir untuk lat/lon
function validateLatLon(lat, lon) {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
  if (Math.abs(lat) > 90) return false;
  if (Math.abs(lon) > 180) return false;
  return true;
}

// --- Coba parse pasangan desimal cepat (semua separator umum)
const DECIMAL_PAIR_RE = /^\s*([-+]?\d+(?:[.,]\d+)?)\s*[,;/ ]\s*([-+]?\d+(?:[.,]\d+)?)\s*$/;

// --- Ekstraksi dua koordinat dari string (mencoba beberapa strategi)
export function parseLatLon(input) {
  // 1) Coba desimal cepat
  const m = input.match(DECIMAL_PAIR_RE);
  if (m) {
    const lat = parseFloat(toDot(m[1]));
    const lon = parseFloat(toDot(m[2]));
    return validateLatLon(lat, lon) ? { lat, lon } : null;
  }

  // 2) Coba cari dua "blok koordinat" (desimal/DMS) berurutan
  //    Tiap blok: angka[° angka[' angka["]] + optional N/S/E/W (awal/akhir blok)
  const BLOCK_RE =
    /(?:\b[NSWE]\s*)?([+-]?\d+(?:[.,]\d+)?(?:\s*[°º]\s*\d+(?:[.,]\d+)?(?:\s*['’]\s*\d+(?:[.,]\d+)?(?:\s*["”])?)?)?)(?:\s*[NSWE])?/ig;

  const blocks = [];
  let b;
  while ((b = BLOCK_RE.exec(input)) !== null) {
    const text = b[0].trim(); // ambil seluruh blok yg mengandung hemi di awal/akhir
    if (text) blocks.push(text);
    if (blocks.length === 2) break;
  }

  // Jika belum dapat 2 blok, fallback split dengan pemisah umum
  if (blocks.length < 2) {
    const parts = input.split(/\s*[;,/]\s*/).filter(Boolean);
    if (parts.length >= 2) blocks.push(parts[0], parts[1]);
  }

  if (blocks.length < 2) return null;

  // Parse masing-masing blok
  const a = parseSingleCoord(blocks[0]);
  const bCoord = parseSingleCoord(blocks[1]);
  if (a.value == null || bCoord.value == null) return null;

  // Tentukan mana lat/lon:
  // - Jika ada hemisphere: N/S => latitude, E/W => longitude
  // - Jika tidak ada hemisphere, asumsikan urutan: [lat, lon]
  let lat, lon;

  const aIsLat = a.hemi === 'N' || a.hemi === 'S';
  const aIsLon = a.hemi === 'E' || a.hemi === 'W';
  const bIsLat = bCoord.hemi === 'N' || bCoord.hemi === 'S';
  const bIsLon = bCoord.hemi === 'E' || bCoord.hemi === 'W';

  if (aIsLat && bIsLon) {
    lat = a.value; lon = bCoord.value;
  } else if (aIsLon && bIsLat) {
    lat = bCoord.value; lon = a.value;
  } else {
    lat = a.value; lon = bCoord.value;
  }

  // Validasi akhir range
  return validateLatLon(lat, lon) ? { lat, lon } : null;
}

export function validateKinksOfFeature(feature) {
  const errors = [];

  const coordinates = feature?.geometry?.coordinates;
  const type = feature?.geometry?.type;

  if (type !== "Point") {
    try {
      // Cek self-intersection
      const kinks_result = kinks(feature);
      if (kinks_result.features.length > 0) {
        errors.push({
          type: 'self-intersection',
          locations: kinks_result.features
        });
      }

      // Cek jumlah titik minimum (minimal 4: 3 titik + closing point)
      const coords = coordinates[0];
      if (coords.length < 2 && type === 'LineString') {
        errors.push({ type: 'insufficient-points' });
      }

      if (type === 'Polygon') {

        if (coords.length < 4) {
          errors.push({ type: 'insufficient-points' });
        }

        // Cek apakah polygon closed
        const firstPoint = coords[0];
        const lastPoint = coords[coords.length - 1];
        if ((firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1])) {
          errors.push({ type: 'not-closed' });
        }

        // Cek area (polygon valid harus punya area > 0)
        const area_result = area(feature);
        if (area_result === 0) {
          errors.push({ type: 'zero-area' });
        }
      }

    } catch (e) {
      errors.push({
        type: 'invalid-geometry',
        message: e.message
      });
    }
  }

  return {
    is_valid: errors.length === 0,
    errors: errors
  };

}